import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Textarea from 'src/components/Common/@electron/Textarea';
import { JurisdictionCodesType } from 'src/components/Common/Jurisdiction/types';
import setJurisdictionCookie from 'src/lib/Utils/setJurisdictionCookie';
import { ORRebateType, Step6FormProps } from '../types';
import { FormStepButtons } from './Buttons';
import CarolinasJurisdictionModal from './Step6Modals/CarolinasJurisdictionModal';
import RebatesModal from './Step6Modals/RebatesModal';
import { checkJurisdiction } from './Step6Modals/util/checkJurisdiction';
import { getRebates } from './Step6Modals/util/getRebates';

export const Step6Form = ({
  goBack,
  handleCancel,
  defaultValue,
  handleFormSubmit,
  fetchIsPending,
  formData,
  service,
  stateCode,
  preselectedOffer,
  incomingJurisdiction,
  sendRebateFoundModalAnalytics,
  isGenerateReferralsButtonDisabled,
  foundRebateModalText,
  jurisdictionModalText,
}: Step6FormProps) => {
  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
    clearErrors,
    watch,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      serviceDescriptionInput: defaultValue || '',
    },
  });
  const [jurisdiction, setJurisdiction] = useState<JurisdictionCodesType | undefined>(undefined);
  const [regionSelectModalIsOpen, setRegionSelectModalIsOpen] = useState(false);
  const [rebateModalIsOpen, setRebateModalIsOpen] = useState(false);
  const [rebates, setRebates] = useState<ORRebateType[]>([]);
  const [value, setValue] = useState('');

  const hasError = !!errors?.serviceDescriptionInput?.type;
  const maxInputNum = Number(formData.maxInputLength);
  const maxInputLength = !isNaN(maxInputNum) && maxInputNum > 0 ? maxInputNum : undefined;
  const registerObj = maxInputLength
    ? {
        required: {
          value: formData.inputRequired,
          message: formData.inputRequiredErrorMessage || '',
        },
        maxLength: {
          value: maxInputLength,
          message: formData.maxInputErrorMessage || '',
        },
      }
    : {
        required: {
          value: formData.inputRequired,
          message: formData.inputRequiredErrorMessage || '',
        },
      };
  const rebateIcon = service.img;

  const handlePrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    const data = getValues('serviceDescriptionInput');
    goBack(data);
  };

  const onSubmit = async () => {
    setValue(getValues('serviceDescriptionInput'));
    // if the user selected a rebate before step3, no need to check for rebates just submit the form
    if (preselectedOffer) {
      handleFormSubmit(value);
    } else if (incomingJurisdiction?.includes(stateCode)) {
      // if jurisdiction has been passed down from FindContractor and matches state selected in dropdown, set jurisdiction value from incomingJurisdiction
      // setting the jurisdiction will trigger checkRebates()
      setJurisdiction(incomingJurisdiction);
    } else {
      // if jurisdiction did not come down from FindContractor, set jurisdiction based on state selection in Step 3
      // setting the jurisdiction will trigger checkRebates()
      checkJurisdiction({ stateCode, setRegionSelectModalIsOpen, setJurisdiction });
    }
  };

  // when jurisdiction is updated, get rebates for the selected jurisdiction
  // if the selected jurisdiction has no rebates, submit the form and take the user to the results page
  useEffect(() => {
    if (jurisdiction) {
      setJurisdictionCookie(jurisdiction);
      getRebates({
        jurisdiction,
        service,
        value,
        setRebates,
        setRebateModalIsOpen,
        handleFormSubmit,
      });
    }
  }, [jurisdiction]);

  // when user closes rebates modal, submit the form and take the user to the results page
  const closeRebateModal = () => {
    setRebateModalIsOpen(false);
    handleFormSubmit(value);
  };

  return (
    <div className="bg-white max-w-md rounded-md mx-12 sm:mx-auto p-16 pt-32 my-24 text-gray-dark text-left">
      <form noValidate>
        <Textarea
          id="FindContractorServiceDescriptionInput"
          label={formData.label}
          onKeyUp={() => {
            if (hasError) {
              clearErrors('serviceDescriptionInput');
            }
          }}
          {...register('serviceDescriptionInput', registerObj)}
          maxLength={maxInputLength}
          currentLength={watch('serviceDescriptionInput', '').length}
          status={hasError ? 'error' : undefined}
          errorMessage={errors.serviceDescriptionInput?.message}
        />
        <p className="mb-24 mt-12">{formData.description}</p>
        <FormStepButtons
          handlePreviousClick={handlePrevious}
          handleContinueClick={handleSubmit(onSubmit)}
          isPending={fetchIsPending}
          handleCancelClick={handleCancel}
          continueButtonText={formData.continueButtonText}
          previousButtonText={formData.previousButtonText}
          cancelButtonText={formData.cancelButtonText}
          isContinueButtonDisabled={isGenerateReferralsButtonDisabled}
        />
      </form>

      <RebatesModal
        title={foundRebateModalText.title}
        primaryButtonText={foundRebateModalText.primaryButtonText}
        secondaryButtonText={foundRebateModalText.secondaryButtonText}
        titleVariant="text-center"
        id="rebate-found"
        isOpen={rebateModalIsOpen}
        onClose={closeRebateModal}
        rebates={rebates}
        rebateIcon={rebateIcon}
        sendRebateFoundModalAnalytics={sendRebateFoundModalAnalytics}
      />

      <CarolinasJurisdictionModal
        title={jurisdictionModalText.title?.replace(
          // eslint-disable-next-line no-template-curly-in-string
          '${state}',
          `${stateCode === 'NC' ? 'North Carolina' : 'South Carolina'}`
        )}
        titleVariant="text-center"
        description={jurisdictionModalText.description}
        descriptionVariant="text-center"
        id="region-select"
        isOpen={regionSelectModalIsOpen}
        variant="no-close-button"
        stateCode={stateCode}
        setRegionSelectModalIsOpen={setRegionSelectModalIsOpen}
        setJurisdiction={setJurisdiction}
      />
    </div>
  );
};
